/**
 * Pairing Roles — the capability tier attached to a single desktop⇄user binding.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ THIS FILE IS BYTE-IDENTICAL in frontend/core/ and desktop/core/.         │
 * │ The monorepo has no shared package, so producer and consumer each keep a │
 * │ copy. Edit BOTH (or re-copy) — never just one.                          │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * A pairing code is generated with a role; the desktop that consumes it is
 * bound at that role. From then on the role travels on the binding and gates
 * which feature commands that pairing may issue:
 *
 *   - Desktop enforcement (authoritative): feature_message commands whose
 *     `feature` is denied by the sender's role are dropped before dispatch.
 *     This is the only tamper-proof gate — feature traffic is P2P and never
 *     reaches the signaling backend, so the desktop is where "PairB can't spin
 *     the wheel" is actually enforced.
 *   - Frontend UI gating (cosmetic): the sidebar/menu hides features the
 *     selected device's role can't use, so the operator never sees them.
 *
 * MODEL: default-ALLOW with a per-role denylist. Reads, queries and infra
 * namespaces are allowed unless a role explicitly denies them, so a monitor
 * pairing can never be broken by an observation command we forgot to list. A
 * role only ever *removes* capability from the full set.
 *
 * Loads in three environments from ONE source (no import/export keywords), the
 * same UMD shape as contracts.js:
 *   - Frontend (Vite/ESM): via the `roles.esm.js` re-export shim, or
 *     globalThis.InquisitorRoles.
 *   - Desktop renderer (window <script>): window.InquisitorRoles.
 *   - Desktop main (CJS): require('../core/roles.js') → module.exports.
 */
;(function (global, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.InquisitorRoles = api;
  if (typeof globalThis !== 'undefined') globalThis.InquisitorRoles = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  // Inflicting / enforcement / stakes features — the "act on them" surface.
  // Denying this set turns a pairing into a passive observer.
  const PUNISH_FEATURES = [
    'spin-wheel', 'sound-blaster', 'write-lines', 'input-sabotage',
    'window-rearrange', 'countdown-timer', 'phantom-cursor', 'wallpaper-hijack',
    'quiz', 'eye-lock', 'screen-tint',
    'economy', 'user-panel', 'forced-browser',
    'time-limit', 'url-block', 'file-block',
    'challenges', 'labyrinth', 'siege', 'word-search', 'task-board',
    'keyword-database', 'pishock',
  ];

  // Two-way / compel-a-response features. Denying these on top of PUNISH leaves
  // a pure watch-only pairing.
  const COMMS_FEATURES = [
    'chat', 'interrogation', 'journal', 'quick-confession', 'ai-voice',
  ];

  // Ordered most-privileged → least. `deny` lists the feature namespaces a
  // pairing at this role may NOT command; everything else is permitted.
  const ROLES = {
    full: {
      id: 'full',
      label: 'Full Control',
      description: 'Everything — surveillance, communication, and all punishments.',
      deny: [],
    },
    interrogator: {
      id: 'interrogator',
      label: 'Observe & Communicate',
      description: 'Watch everything and talk to them, but cannot inflict punishments.',
      deny: PUNISH_FEATURES.slice(),
    },
    monitor: {
      id: 'monitor',
      label: 'Observe Only',
      description: 'Read-only surveillance. No communication, no punishments.',
      deny: PUNISH_FEATURES.concat(COMMS_FEATURES),
    },
  };

  const DEFAULT_ROLE = 'full';

  // Pre-build deny lookups once.
  const DENY_SETS = {};
  Object.keys(ROLES).forEach((id) => {
    DENY_SETS[id] = new Set(ROLES[id].deny);
  });

  /** Is `roleId` a role we know about? */
  function isValidRole(roleId) {
    return typeof roleId === 'string' && Object.prototype.hasOwnProperty.call(ROLES, roleId);
  }

  /** Coerce any input (null/unknown/legacy) to a concrete role id. */
  function normalizeRole(roleId) {
    return isValidRole(roleId) ? roleId : DEFAULT_ROLE;
  }

  /** The role descriptor, falling back to the default role. */
  function getRole(roleId) {
    return ROLES[normalizeRole(roleId)];
  }

  /** Roles as an ordered array (most → least privileged) for pickers. */
  function listRoles() {
    return Object.keys(ROLES).map((id) => ROLES[id]);
  }

  /**
   * May a pairing at `roleId` command `featureId`?
   * Default-allow: only an explicit deny entry blocks it. Missing/unknown
   * feature ids are allowed (infra/read namespaces are never denied).
   */
  function roleAllowsFeature(roleId, featureId) {
    if (!featureId) return true; // no feature namespace to gate (e.g. pure-FE games)
    const deny = DENY_SETS[normalizeRole(roleId)];
    return !deny.has(featureId);
  }

  return {
    ROLES,
    DEFAULT_ROLE,
    PUNISH_FEATURES: PUNISH_FEATURES.slice(),
    COMMS_FEATURES: COMMS_FEATURES.slice(),
    isValidRole,
    normalizeRole,
    getRole,
    listRoles,
    roleAllowsFeature,
  };
});
